import React from "react";
import { Office } from "common";
import LogoutFooter from "taskpane/components/logoutFooter";
import ConferenceSettings from "taskpane/components/conferenceSettings";
import ConferenceHeader from "taskpane/components/conferenceHeader";
import { CustomProperty, LocalStorageItem, RoutePath, AccessBitPosition } from "taskpane/models/enums";
import { useQueryGetConference } from "taskpane/hooks/queries";
import { IGetConference, IGetConferenceResponse } from "taskpane/hooks/queries/useQueryGetConference";
import { API } from "taskpane/constants/api";
import { getBitByPosition } from "taskpane/utils";
import { IConferenceQueryResult } from "taskpane/models/interfaces";
import { getQueryErrorDeauthorization, getQueryErrorTryAgainLater } from "taskpane/resources";
import { useLoading } from "taskpane/providers/loadingProvider";
import "../styles/createConferencePage.css";
import { useHistory } from "react-router-dom";
import { useApiContext } from "taskpane/providers/apiProvider";

const CreateConferencePage: React.FC = (): React.JSX.Element => {
  const email: string = localStorage.getItem(LocalStorageItem.email);
  const history = useHistory();
  const { setLoading } = useLoading();
  const { apiUrl, apiVersion, fetchConfig } = useApiContext();
  const [conferenceId, setConferenceId] = React.useState<string>("");
  const [accessMicro, setAccessMicro] = React.useState<0 | 1>(0);
  const [accessVideo, setAccessVideo] = React.useState<0 | 1>(0);
  const [accessWaitingHall, setAccessWaitingHall] = React.useState<0 | 1>(0);
  const [accessDemonstration, setAccessDemonstration] = React.useState<0 | 1>(0);
  const [errorText, setErrorText] = React.useState<string>("");
  const setAccessSettingsByNumber = (permissions: number) => {
    setAccessMicro(getBitByPosition(permissions, AccessBitPosition.micro));
    setAccessVideo(getBitByPosition(permissions, AccessBitPosition.video));
    setAccessDemonstration(getBitByPosition(permissions, AccessBitPosition.demonstration));
    setAccessWaitingHall(getBitByPosition(permissions, AccessBitPosition.waitingHall));
  };
  const getConferenceSettings = (apiUrl: string, apiVersion: number) => {
    Office.context.mailbox.item.loadCustomPropertiesAsync(
      (asyncResult: Office.AsyncResult<Office.CustomProperties>) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          setAccessSettingsByNumber(API.defaultPermission);
          const errorPlace: string = "при попытке получения идентификатора конференции.";
          const error: string = "Настройки конференции выставлены по умолчанию";
          setErrorText(getQueryErrorTryAgainLater(`${errorPlace} ${error}`));
          return setLoading(false);
        }

        const confId: string = asyncResult.value.get(CustomProperty.conferenceId);

        if (!confId) {
          setAccessSettingsByNumber(API.defaultPermission);
          return setLoading(false);
        }

        setConferenceId(confId);

        const token: string = localStorage.getItem(LocalStorageItem.token);
        const queryGetConferenceParams: IGetConference = {
          token: token,
          apiUrl: apiUrl,
          conferenceId: confId,
          email: email,
        };

        useQueryGetConference(queryGetConferenceParams, (result: IConferenceQueryResult<IGetConferenceResponse>) => {
          setLoading(false);

          const errorMessage = result.errorMessage;

          if (errorMessage) {
            return history.push(RoutePath.loginPage, { errorMessage });
          }

          if (result.response && apiVersion >= API.versionWithSettings) {
            const permissions: number = result.response.results.roles.default.permissions;

            setAccessSettingsByNumber(permissions);
          }
        });
      }
    );
  };

  React.useEffect(() => {
    const domain: string = localStorage.getItem(LocalStorageItem.domain);

    if (!domain) {
      const errorMessage: string = getQueryErrorDeauthorization();

      return history.push(RoutePath.loginPage, { errorMessage });
    }

    setLoading(true);

    if (!apiUrl || !apiVersion) {
      fetchConfig(domain, ({ apiUrl, apiVersion, errorMessage }) => {
        if (errorMessage) {
          setLoading(false);
          return history.push(RoutePath.loginPage, { errorMessage });
        }

        getConferenceSettings(apiUrl, apiVersion);
      });
    } else {
      getConferenceSettings(apiUrl, apiVersion);
    }
  }, []);

  return (
    <div className="conferenceContainer" data-testid="conferencePage">
      <div>
        <ConferenceHeader
          errorText={errorText}
          conferenceId={conferenceId}
          setConferenceId={setConferenceId}
          accessMicro={accessMicro}
          accessVideo={accessVideo}
          accessWaitingHall={accessWaitingHall}
          accessDemonstration={accessDemonstration}
        />
        {apiVersion >= API.versionWithSettings ? (
          <ConferenceSettings
            accessMicro={Boolean(accessMicro)}
            accessVideo={Boolean(accessVideo)}
            accessWaitingHall={Boolean(accessWaitingHall)}
            accessDemonstration={Boolean(accessDemonstration)}
            setAccessMicro={setAccessMicro}
            setAccessVideo={setAccessVideo}
            setAccessWaitingHall={setAccessWaitingHall}
            setAccessDemonstration={setAccessDemonstration}
          />
        ) : null}
      </div>
      <LogoutFooter />
    </div>
  );
};

export default CreateConferencePage;
